import React, { useState, useEffect, useMemo, useRef } from "react"
import clsx from "clsx"
import { showConfirm } from "@/components/Confirm"
import { motion, AnimatePresence } from "motion/react"
import styles from "./index.module.scss"
import { i18n, I18nTextPath } from "@/i18n"
import { BOARD_SIZE, useGameStore, MIN_STEP } from "@/store/game"
import { useShareStore } from "@/store/share"
import { useAnimationStore } from "@/store/animation"
import { calculateAIMove } from "@/services/AIService/index"
import { calculateScore } from "@/utils/game/score"
import {
    convertToAIState,
    getAllValidMovesFromState,
} from "@/services/AIService/goUtils"
import { showSkillDialog, Skill } from "@/components/SkillDialog"
import {
    numberToChinese,
    hasCompletedStage,
    setGuideProgress,
    GuideStage,
} from "@/utils/utils"
import { useDialogue } from "@/hooks/useDialogue"
import { ChessAnimation } from "@/components/ChessAnimation"
import { playSkillAnimation } from "@/components/SkillAnimation"
import blackChess from "@/assets/imgs/game/black_chess.png"
import whiteChess from "@/assets/imgs/game/white_chess.png"
import {
    shareFailedDialogueData,
    shareSuccessDialogueData,
} from "@/components/Dialog/data"
import ResultShareModal from "./ResultShareModal"
import { RecallButton } from "../RecallButton"
import { collect } from "@/utils/sdk"
import NumberFlow from "@number-flow/react"
import Toast from "@/components/Toast"
import { completeShare } from "@hg/activity-utils"
import { sfxPlay } from "@/assets/audios/sfx"

export const MainGame = () => {
    const showDotsPosition = [
        [2, 2],
        [2, 6],
        [4, 4],
        [6, 2],
        [6, 6],
    ]
    const [isAIThinking, setIsAIThinking] = useState(false)
    const [isUsingSkill, setIsUsingSkill] = useState(false)
    const boardRef = useRef<HTMLDivElement>(null)
    const infoRef = useRef<HTMLDivElement>(null)
    const blackTopRef = useRef<HTMLDivElement>(null)
    const whiteTopRef = useRef<HTMLDivElement>(null)
    const {
        board,
        currentPlayer,
        placeChess,
        undo,
        reset,
        blackCaptures,
        whiteCaptures,
        currentStep,
        chooseSkills,
        setChooseSkills,
        useSkill,
        clearActiveSkill,
        activeSkillIndex,
        usedSkillIndices,
        resetGame,
        setGameStartTime,
        winner,
        scoreDiff,
    } = useGameStore()

    const prevBlackCapturesRef = useRef(blackCaptures)
    const prevWhiteCapturesRef = useRef(whiteCaptures)
    const [isShowShareModal, setIsShowShareModal] = useState(false)
    const { activeAnimations } = useAnimationStore()
    const [isGameEnd, setIsGameEnd] = useState(false)
    const prevBoardRef = useRef<typeof board>(board)
    const [isChooseSkills, setIsChooseSkills] = useState(false) //选技能是否结束
    const {
        startGameDialogue,
        passWithoutSkillDialogue,
        firstUseSkillDialogue,
        randomSkillDialogue,
        showShareDialogue,
        startUseGuideDialogue,
    } = useDialogue()
    const { setShareData } = useShareStore()
    useEffect(() => {
        setIsGameEnd(false)
        setIsChooseSkills(false)
        startGameDialogue().then(() => {
            showSkillDialog().then((chooseSkills) => {
                setIsChooseSkills(true)
                //埋点上报
                const startTime = Date.now()
                setGameStartTime(startTime)
                collect("mini_game_start", {
                    game_start_ts: startTime,
                })

                setChooseSkills(chooseSkills)
                if (!hasCompletedStage(GuideStage.SKILL_USE_GUIDE)) {
                    startUseGuideDialogue()
                    setGuideProgress(GuideStage.SKILL_USE_GUIDE)
                }
            })
        })
    }, [resetGame])

    // 监听 blackCaptures 变化，触发扫光动画
    useEffect(() => {
        if (blackCaptures !== prevBlackCapturesRef.current) {
            prevBlackCapturesRef.current = blackCaptures
            if (blackTopRef.current) {
                // 先移除类（如果存在），确保可以重复触发
                blackTopRef.current.classList.remove(styles.shine)
                // 使用 requestAnimationFrame 确保 DOM 更新后再添加类
                requestAnimationFrame(() => {
                    if (blackTopRef.current) {
                        blackTopRef.current.classList.add(styles.shine)
                        // 动画结束后移除类，以便下次可以再次触发
                        setTimeout(() => {
                            if (blackTopRef.current) {
                                blackTopRef.current.classList.remove(
                                    styles.shine
                                )
                            }
                        }, 1000) // 动画时长 1 秒
                    }
                })
            }
        }
        // styles.shine 是常量，不需要在依赖数组中
    }, [blackCaptures])

    // 监听 whiteCaptures 变化，触发扫光动画
    useEffect(() => {
        if (whiteCaptures !== prevWhiteCapturesRef.current) {
            prevWhiteCapturesRef.current = whiteCaptures
            if (whiteTopRef.current) {
                // 先移除类（如果存在），确保可以重复触发
                whiteTopRef.current.classList.remove(styles.shine)
                // 使用 requestAnimationFrame 确保 DOM 更新后再添加类
                requestAnimationFrame(() => {
                    if (whiteTopRef.current) {
                        whiteTopRef.current.classList.add(styles.shine)
                        // 动画结束后移除类，以便下次可以再次触发
                        setTimeout(() => {
                            if (whiteTopRef.current) {
                                whiteTopRef.current.classList.remove(
                                    styles.shine
                                )
                            }
                        }, 1000) // 动画时长 1 秒
                    }
                })
            }
        }
        // styles.shine 是常量，不需要在依赖数组中
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [whiteCaptures])

    // 更新上一次的 board 状态，用于检测棋子消失
    useEffect(() => {
        prevBoardRef.current = board.map((r) => [...r])
    }, [board])

    // 监听游戏结束，显示结束对话
    useEffect(() => {
        if (winner) {
            sfxPlay("GAME_END")
            if (winner === "black" && !usedSkillIndices.length) {
                setTimeout(() => {
                    passWithoutSkillDialogue()
                }, 1200)
            }
            // 从 store 中获取最新的 gameStartTime，确保拿到更新后的值
            const currentGameStartTime = useGameStore.getState().gameStartTime
            collect("mini_game_end", {
                game_start_ts: currentGameStartTime,
            })
            setIsGameEnd(true)
        }
    }, [winner])

    const isBlackWinner = useMemo(() => {
        return winner === "black"
    }, [winner])

    const isWhiteWinner = useMemo(() => {
        return winner === "white"
    }, [winner])

    // 兜底策略：获取所有合法的落子位置（基于通用 go 工具函数）
    const getAllValidMoves = (): Array<{ row: number; col: number }> => {
        const { board, currentPlayer, koPosition } = useGameStore.getState()
        return getAllValidMovesFromState(board, currentPlayer, koPosition)
    }

    // 尝试使用兜底策略落子（会尝试所有合法位置直到成功）
    const tryFallbackMove = (): boolean => {
        const validMoves = getAllValidMoves()

        if (validMoves.length === 0) {
            return false
        }

        // 打乱顺序，随机尝试
        const shuffled = [...validMoves].sort(() => Math.random() - 0.5)

        // 尝试所有合法位置
        for (let i = 0; i < shuffled.length; i++) {
            const move = shuffled[i]
            const result = placeChess(move.row, move.col)
            if (result.success) {
                return true
            }
        }

        return false
    }

    // 当 AI 无法再落子时，直接结算棋局
    const endGameDueToNoMove = () => {
        const { board, blackCaptures, whiteCaptures } = useGameStore.getState()
        const { winner, diff } = calculateScore(
            board,
            blackCaptures,
            whiteCaptures
        )
        useGameStore.setState({
            winner,
            scoreDiff: diff,
        })
    }

    const handleAIPress = async () => {
        const { winner } = useGameStore.getState()
        if (winner) {
            return
        }
        setIsAIThinking(true)

        // 获取当前状态并转换为AI状态
        const storeState = useGameStore.getState()
        const aiState = convertToAIState(storeState)

        try {
            // 记录开始时间
            const startTime = Date.now()

            // 尝试使用主 AI 计算（WASM 或 JS，取决于构建配置）
            let move: { row: number; col: number } | null = null
            try {
                move = await calculateAIMove(aiState)
            } catch (aiError) {
                // AI 计算失败，使用兜底策略
                const success = tryFallbackMove()
                const elapsedTime = Date.now() - startTime
                const targetTotalTime = Math.random() * 1000 + 1000 // 1000-2000ms
                if (elapsedTime < targetTotalTime) {
                    const remainingDelay = targetTotalTime - elapsedTime
                    await new Promise((resolve) =>
                        setTimeout(resolve, remainingDelay)
                    )
                }
                if (!success) {
                    endGameDueToNoMove()
                }
                return
            }

            // 验证move是否有效
            if (!move) {
                const success = tryFallbackMove()
                const elapsedTime = Date.now() - startTime
                const targetTotalTime = Math.random() * 1000 + 1000 // 1000-2000ms
                if (elapsedTime < targetTotalTime) {
                    const remainingDelay = targetTotalTime - elapsedTime
                    await new Promise((resolve) =>
                        setTimeout(resolve, remainingDelay)
                    )
                }
                if (!success) {
                    endGameDueToNoMove()
                }
                return
            }

            // 验证 move 合法性（边界与占用）
            if (
                move.row < 0 ||
                move.row >= BOARD_SIZE ||
                move.col < 0 ||
                move.col >= BOARD_SIZE
            ) {
                if (!tryFallbackMove()) {
                    endGameDueToNoMove()
                }
            } else {
                // 检查位置是否已有棋子
                const { board } = useGameStore.getState()
                if (board[move.row][move.col]) {
                    const success = tryFallbackMove()
                    if (!success) {
                        endGameDueToNoMove()
                    }
                    return
                }
            }

            // 使用 AI 返回的 move 落子
            if (!isGameEnd) {
                // 计算已用时间
                const elapsedTime = Date.now() - startTime
                // 目标总时间：1-2 秒随机
                const targetTotalTime = Math.random() * 1000 + 1000 // 1000-2000ms
                // 如果已用时间小于目标时间，则补充延迟
                if (elapsedTime < targetTotalTime) {
                    const remainingDelay = targetTotalTime - elapsedTime
                    await new Promise((resolve) =>
                        setTimeout(resolve, remainingDelay)
                    )
                }

                // 尝试落子
                const result = placeChess(move.row, move.col)
                if (!result.success) {
                    // 如果落子失败，使用兜底策略；仍然无子可下则直接结算
                    const fallbackSuccess = tryFallbackMove()
                    if (!fallbackSuccess) {
                        endGameDueToNoMove()
                    }
                }
            }
        } catch (error) {
            try {
                const success = tryFallbackMove()
                if (!success) {
                    endGameDueToNoMove()
                }
            } catch (fallbackError) {}
        } finally {
            setIsAIThinking(false)
            // 胜负状态会自动更新到 store 中，直接使用 winner 和 scoreDiff
        }
    }

    const handleClick = async (row: number, col: number) => {
        // 只有当前允许落子时才响应点击
        if (!canUserInteract || !isChooseSkills) {
            return
        }

        const { activeSkillIndex, validateMove } = useGameStore.getState()

        // 先用 store 的统一规则检查本次落子是否合法
        const checkResult = validateMove(row, col)
        if (!checkResult.success) {
            if (checkResult.reason === "ko") {
                Toast.message(i18n.get("game.koForbidden"))
            } else if (checkResult.reason === "occupied") {
                Toast.message(i18n.get("game.positionOccupied"))
            } else if (checkResult.reason === "outOfBounds") {
                Toast.message(i18n.get("game.positionOutOfBounds"))
            } else if (checkResult.reason === "suicide") {
                Toast.message(i18n.get("game.suicideForbidden"))
            }
            return
        }

        // 规则检查通过后，再播放技能动画并真正落子
        if (activeSkillIndex !== null) {
            setIsUsingSkill(true)
            try {
                await playSkillAnimation(chooseSkills[activeSkillIndex], {
                    row,
                    col,
                })
            } catch (error) {
                console.error("playSkillAnimation error", error)
            }
        }

        const result = placeChess(row, col)

        // 玩家下棋成功后，立即触发AI下棋
        if (result.success) {
            //只有技能1和5需要序列帧动画，因此他们的ai下棋需要在动画播放完
            const needChessAnimation = [Skill.SKILL1, Skill.SKILL5]
            const { history } = useGameStore.getState()
            const lastMove = history[history.length - 1]

            if (!lastMove.skill) {
                // 没有使用技能，直接让 AI 思考
                handleAIPress()
            } else if (!needChessAnimation.includes(lastMove.skill)) {
                // 使用了技能但不需要序列帧动画，技能动画结束后立刻进入回调
                useSkillCallback()
            } else {
                // 技能 1 / 5 有额外棋盘序列帧动画，这里简单等待 1.5s 再触发回调
                setTimeout(() => {
                    useSkillCallback()
                }, 1500)
            }
        }
    }

    const useSkillCallback = () => {
        // 技能动画和后续流程（包括棋盘序列帧）结束后，恢复可交互
        setIsUsingSkill(false)
        const { usedSkillIndices } = useGameStore.getState()
        if (!usedSkillIndices.length) {
            return
        }
        if (usedSkillIndices.length === 1) {
            firstUseSkillDialogue(handleAIPress)
            return
        }
        randomSkillDialogue(handleAIPress)
    }

    // 公共交互条件：玩家回合 + 未在 AI 思考 + 未在技能动画中
    const canUserInteract =
        currentPlayer && !isAIThinking && !isUsingSkill && !isGameEnd

    const handleClickSkill = (skillIndex: number) => {
        if (!canUserInteract) {
            return
        }
        // 再点一次同一个技能，取消激活
        if (activeSkillIndex === skillIndex) {
            clearActiveSkill()
            return
        }
        // 否则激活技能（下一步落子时生效）
        useSkill(skillIndex)
    }

    const handlePrepareShare = async () => {
        const { winner } = useGameStore.getState()
        const shareDialogue = getRandomShareDialogue()
        // 仅存储分享话术等信息，棋盘和得分全部由 canvas 重新绘制
        setShareData("", "", shareDialogue.text, [shareDialogue], winner)
        try {
            // 展示弹窗时就完成分享任务
            await completeShare({ activityCid: __ACTIVITY_CID, type: "main" })
            sfxPlay("dialog")
            showShareDialogue(() => setIsShowShareModal(true))
            collect("share_button", {
                button_name: "game",
            })
        } catch (error) {
            Toast.message("Failed to share. Please try again later.")
            console.error("修改分享状态出错\n", error)
        }
    }

    const handleRestart = () => {
        if (isAIThinking) {
            return
        }
        showConfirm({
            content: i18n.get("game.restartTip"),
            onOk: () => {
                reset()
                setIsGameEnd(false)
            },
            onCancel: () => {},
        })
    }
    const showShareModal = async () => {
        await handlePrepareShare()
    }

    const getRandomShareDialogue = () => {
        const data = isBlackWinner
            ? shareSuccessDialogueData
            : shareFailedDialogueData
        const randomIndex = Math.floor(Math.random() * data.length)
        return data[randomIndex]
    }

    return (
        <div className={styles.mainGame}>
            <div className={styles.main}>
                <div className={styles.leftPanel}>
                    <div className={styles.board}>
                        <div
                            className={clsx(
                                styles.blackChessBox,
                                currentPlayer && styles.thinking
                            )}
                        />
                        <div id="grid" className={styles.grid} ref={boardRef}>
                            {Array(BOARD_SIZE)
                                .fill(0)
                                .map((_, row) =>
                                    Array(BOARD_SIZE)
                                        .fill(0)
                                        .map((_, col) => (
                                            <div
                                                key={`${row}-${col}`}
                                                className={clsx(
                                                    styles.cell,
                                                    board[row][col]
                                                        ? styles.hasChess
                                                        : "",
                                                    isGameEnd
                                                        ? styles.disabled
                                                        : ""
                                                )}
                                                onClick={() => {
                                                    handleClick(row, col)
                                                }}
                                            >
                                                {showDotsPosition.some(
                                                    ([r, c]) =>
                                                        r === row && c === col
                                                ) && (
                                                    <div
                                                        className={styles.dot}
                                                    />
                                                )}
                                                <AnimatePresence mode="wait">
                                                    {board[row][col] && (
                                                        <motion.div
                                                            initial={{
                                                                scale: 1.5,
                                                                opacity: 0,
                                                            }}
                                                            animate={{
                                                                scale: 1,
                                                                opacity: 1,
                                                            }}
                                                            exit={{
                                                                opacity: 0,
                                                                transition: {
                                                                    duration: 0.3, // 消失动画 0.3s
                                                                },
                                                            }}
                                                            transition={{
                                                                duration: 0.3, // 出现动画 0.5s
                                                            }}
                                                            key={`${row}-${col}`}
                                                            className={
                                                                styles.chess
                                                            }
                                                            style={{
                                                                backgroundImage: `url(${board[row][col] === "black" ? blackChess : whiteChess})`,
                                                            }}
                                                        />
                                                    )}
                                                </AnimatePresence>
                                                {/* 动画层 */}
                                                {activeAnimations.get(
                                                    `${row}-${col}`
                                                ) && (
                                                    <ChessAnimation
                                                        type={
                                                            activeAnimations.get(
                                                                `${row}-${col}`
                                                            )!.type
                                                        }
                                                        color={
                                                            activeAnimations.get(
                                                                `${row}-${col}`
                                                            )!.color
                                                        }
                                                        onComplete={() => {
                                                            useSkillCallback()
                                                        }}
                                                    />
                                                )}
                                            </div>
                                        ))
                                )}
                        </div>

                        <RecallButton stage="mainGame" />
                        {isAIThinking && (
                            <motion.div
                                className={styles.thinkingText}
                                animate={{ opacity: [1, 0.5, 1] }}
                                transition={{
                                    duration: 2,
                                    repeat: Infinity,
                                    ease: "linear",
                                }}
                            >
                                {i18n.get("game.thinking")}
                            </motion.div>
                        )}
                        <div
                            className={clsx(
                                styles.whiteChessBox,
                                !currentPlayer && styles.thinking
                            )}
                        />
                    </div>
                </div>
                <div className={styles.rightPanel}>
                    <div className={styles.infoContainer} ref={infoRef}>
                        {isGameEnd && (
                            <motion.div
                                initial={{ opacity: 0, y: -80, scale: 0.9 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                transition={{
                                    duration: 0.6,
                                    ease: [0.34, 1.56, 0.64, 1], // 弹性缓动
                                }}
                                className={styles.gameResult}
                            >
                                <motion.div
                                    className={styles.title}
                                    initial={{ opacity: 0, scale: 0.8, y: -20 }}
                                    animate={{ opacity: 1, scale: 1, y: 0 }}
                                    transition={{
                                        duration: 0.5,
                                        delay: 0.2,
                                        ease: "easeOut",
                                    }}
                                >
                                    <span>
                                        {isBlackWinner
                                            ? i18n.get("game.blackChess")
                                            : i18n.get("game.whiteChess")}
                                    </span>
                                    <motion.div
                                        className={styles.win}
                                        initial={{
                                            opacity: 0,
                                            scale: 0,
                                            rotate: -180,
                                        }}
                                        animate={{
                                            opacity: 1,
                                            scale: 1,
                                            rotate: 0,
                                        }}
                                        transition={{
                                            duration: 0.6,
                                            delay: 0.4,
                                            ease: "backOut", // 回弹效果
                                        }}
                                    >
                                        {i18n.get("game.win")}
                                    </motion.div>
                                </motion.div>
                                <motion.div
                                    className={styles.score}
                                    initial={{ opacity: 0, x: -30 }}
                                    animate={{ opacity: 0, x: 0 }}
                                    transition={{
                                        duration: 0.5,
                                        delay: 0.6,
                                        ease: "easeOut",
                                    }}
                                >
                                    <motion.div
                                        className={styles.scoreIcon}
                                        initial={{ opacity: 0, scale: 0 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        transition={{
                                            duration: 0.4,
                                            delay: 0.7,
                                            ease: "backOut",
                                        }}
                                    >
                                        {i18n.get("game.win")}
                                    </motion.div>
                                    <motion.div
                                        className={styles.icon}
                                        initial={{ opacity: 0, scale: 0 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        transition={{
                                            duration: 0.3,
                                            delay: 0.8,
                                            ease: "easeOut",
                                        }}
                                    ></motion.div>
                                    <motion.div
                                        className={styles.scoreText}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{
                                            duration: 0.5,
                                            delay: 0.85,
                                            ease: "easeOut",
                                        }}
                                    >
                                        {numberToChinese(Math.abs(scoreDiff))}
                                        <span className={styles.scoreTextUnit}>
                                            {i18n.get("game.point")}
                                        </span>
                                    </motion.div>
                                </motion.div>
                            </motion.div>
                        )}

                        <div className={styles.topContainer}>
                            <AnimatePresence mode="wait">
                                {activeSkillIndex === null ? (
                                    <motion.div
                                        key="captures"
                                        className={styles.captureContainer}
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        transition={{
                                            duration: 0.2,
                                            ease: "easeOut",
                                        }}
                                    >
                                        <div
                                            className={`${styles.captureInfo} ${isWhiteWinner ? styles.disabled : ""}`}
                                        >
                                            <div
                                                ref={blackTopRef}
                                                className={`${styles.top} ${styles.blackTop}`}
                                            />
                                            <div className={styles.bottom}>
                                                {i18n.get("game.captureCount")}
                                            </div>
                                            <div className={styles.count}>
                                                <NumberFlow
                                                    value={blackCaptures}
                                                    format={{
                                                        minimumIntegerDigits: 2,
                                                    }}
                                                />
                                            </div>
                                        </div>
                                        <div
                                            className={`${styles.captureInfo} ${isBlackWinner ? styles.disabled : ""}`}
                                        >
                                            <div
                                                ref={whiteTopRef}
                                                className={`${styles.top} ${styles.whiteTop}`}
                                            />
                                            <div className={styles.bottom}>
                                                {i18n.get("game.captureCount")}
                                            </div>
                                            <div className={styles.count}>
                                                <NumberFlow
                                                    value={whiteCaptures}
                                                    format={{
                                                        minimumIntegerDigits: 2,
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </motion.div>
                                ) : (
                                    <motion.div
                                        key={`tip-${chooseSkills[activeSkillIndex]}`}
                                        className={styles.tip}
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        transition={{
                                            duration: 0.2,
                                            ease: "easeOut",
                                        }}
                                    >
                                        {i18n.get(
                                            `game.${chooseSkills[activeSkillIndex]}` as I18nTextPath
                                        )}
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                        {!isGameEnd && (
                            <div className={styles.skillContainer}>
                                <div className={styles.divider} />
                                <div className={styles.name}>
                                    {/* {i18n.get("game.skill")} */}
                                </div>
                                {chooseSkills.map((skill, index) => (
                                    <div
                                        key={index}
                                        className={clsx(
                                            styles.skillItem,
                                            skill === Skill.EMPTY &&
                                                styles.empty,
                                            usedSkillIndices.includes(index)
                                                ? styles.disabled
                                                : "",
                                            activeSkillIndex === index &&
                                                styles.active
                                        )}
                                        onClick={() => {
                                            sfxPlay("click")
                                            handleClickSkill(index)
                                        }}
                                    >
                                        <div
                                            className={clsx(
                                                styles.choosedBg,
                                                skill === Skill.EMPTY &&
                                                    styles.hidden,
                                                activeSkillIndex === index &&
                                                    styles.active
                                            )}
                                        />
                                        <AnimatePresence>
                                            <motion.img
                                                key={skill}
                                                className={styles.skillIcon}
                                                src={require(
                                                    `@/assets/imgs/skills/${skill}.png`
                                                )}
                                                initial={{
                                                    opacity: 0,
                                                    scale: 0.8,
                                                }}
                                                animate={{
                                                    opacity: 1,
                                                    scale: 1,
                                                }}
                                                exit={{ opacity: 0 }}
                                                transition={{
                                                    duration: 0.2,
                                                    ease: "easeOut",
                                                }}
                                            />
                                        </AnimatePresence>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className={styles.operationContainer}>
                        {isGameEnd ? (
                            <div
                                className={`${styles.operationButton} ${styles.share}`}
                                onClick={() => {
                                    sfxPlay("click")
                                    showShareModal()
                                }}
                            >
                                <div className={styles.operationTop}></div>
                                <div className={styles.operationBottom}>
                                    {i18n.get("game.share")}
                                </div>
                            </div>
                        ) : (
                            <div
                                className={`${styles.operationButton} ${styles.revoke} ${currentStep <= MIN_STEP || !canUserInteract ? styles.disabled : ""}`}
                                onClick={() => {
                                    sfxPlay("click")
                                    if (
                                        canUserInteract &&
                                        currentStep > MIN_STEP
                                    ) {
                                        undo()
                                    }
                                }}
                            >
                                <div className={styles.operationTop}></div>
                                <div className={styles.operationBottom}>
                                    {i18n.get("game.revoke")}
                                </div>
                            </div>
                        )}
                        <div
                            className={`${styles.operationButton} ${styles.restart}`}
                            onClick={() => {
                                sfxPlay("click")
                                handleRestart()
                            }}
                        >
                            <div className={styles.operationTop}></div>
                            <div className={styles.operationBottom}>
                                {i18n.get("game.reset")}
                            </div>
                        </div>
                    </div>
                </div>
                <ResultShareModal
                    open={isShowShareModal}
                    onClose={() => setIsShowShareModal(false)}
                ></ResultShareModal>
            </div>
        </div>
    )
}
